import React, { useMemo, useRef } from "react";
import {
	Box,
	Button,
	TextField,
	Typography,
	Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const keyType = (keyMeta, key) => {
	if (keyMeta?.type) return keyMeta.type;
	if (/^signature\d+$/i.test(key || "")) return "signature";
	if (/^text\d+$/i.test(key || "")) return "static_text";
	return "dynamic";
};

const OverlayConfigPanel = ({
	selectedKey,
	availableKeys = [],
	staticValues = {},
	assets = {},
	onStaticValueChange,
	onUploadSignature,
	onDeleteSignature,
	isSubmitting,
}) => {
	const fileInputRef = useRef(null);

	const keyMeta = useMemo(() => {
		return (availableKeys || []).find((k) => (typeof k === "string" ? k : k.key) === selectedKey);
	}, [availableKeys, selectedKey]);

	const type = keyType(keyMeta, selectedKey);

	if (!selectedKey) {
		return (
			<Box mt={2}>
				<Typography variant="subtitle2" gutterBottom>
					Static text & signatures
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Select a <strong>text1–text3</strong> or <strong>signature1–signature3</strong> placeholder
					to set its fixed value or upload an image.
				</Typography>
			</Box>
		);
	}

	if (type === "static_text") {
		return (
			<Box mt={2}>
				<Typography variant="subtitle2" gutterBottom>
					Static text — {selectedKey}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					This exact value is stamped on every filled agreement at the placed position.
				</Typography>
				<TextField
					size="small"
					fullWidth
					label="Fixed text"
					placeholder="e.g. RentBeta"
					value={staticValues[selectedKey] ?? ""}
					onChange={(e) => onStaticValueChange(selectedKey, e.target.value)}
				/>
			</Box>
		);
	}

	if (type === "signature") {
		const asset = assets?.[selectedKey];
		return (
			<Box mt={2}>
				<Typography variant="subtitle2" gutterBottom>
					Signature — {selectedKey}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					Upload a signature image. It will always appear at this position when the PDF is filled.
				</Typography>
				{asset?.url ? (
					<Box
						component="img"
						src={asset.url}
						alt={selectedKey}
						sx={{
							display: "block",
							maxWidth: "100%",
							maxHeight: 80,
							mb: 1,
							border: "1px solid",
							borderColor: "divider",
							bgcolor: "grey.50",
							p: 0.5,
						}}
					/>
				) : (
					<Chip size="small" label="No image uploaded" sx={{ mb: 1 }} />
				)}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/png,image/jpeg,image/webp,image/gif"
					hidden
					onChange={(e) => {
						const file = e.target.files?.[0];
						e.target.value = "";
						if (file) onUploadSignature(selectedKey, file);
					}}
				/>
				<Box display="flex" gap={1} flexWrap="wrap">
					<Button
						size="small"
						variant="outlined"
						startIcon={<UploadFileIcon />}
						disabled={isSubmitting}
						onClick={() => fileInputRef.current?.click()}
					>
						{asset?.url ? "Replace image" : "Upload image"}
					</Button>
					{asset?.url && (
						<Button
							size="small"
							color="error"
							startIcon={<DeleteOutlineIcon />}
							disabled={isSubmitting}
							onClick={() => onDeleteSignature(selectedKey)}
						>
							Remove
						</Button>
					)}
				</Box>
			</Box>
		);
	}

	return (
		<Box mt={2}>
			<Typography variant="subtitle2" gutterBottom>
				Dynamic variable — {selectedKey}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				Filled from landlord/tenant data at preview and onboarding time.
			</Typography>
		</Box>
	);
};

export default OverlayConfigPanel;
